import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, ShoppingCart, FileText, Check, Edit2, Save, RefreshCw, Printer, XCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Modal from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import proposalService from '../services/proposalService';

const STAGES = ['REQUIREMENTS', 'TECHNICAL', 'COMMERCIAL', 'COMPLETED'];
const STAGE_NAMES = {
  'REQUIREMENTS': '1. Requerimientos',
  'TECHNICAL': '2. Técnico',
  'COMMERCIAL': '3. Comercial',
  'COMPLETED': 'Completada'
};

const ProposalDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  const chatEndRef = useRef(null);

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  
  // ===== Funciones Auxiliares =====

  const handleRejectProposal = async () => {
    if (!window.confirm("¿Estás seguro de que deseas marcar esta propuesta como RECHAZADA?")) return;
    try {
      const response = await proposalService.updateProposal(id, { status: 'REJECTED' });
      showNotification('Propuesta rechazada y cerrada', 'success');
      loadProposal();
    } catch (error) {
      showNotification(error.message || 'Error al rechazar propuesta', 'error');
    }
  };
  
  const printDocument = (title, content) => {
    const printWin = window.open('', '', 'width=800,height=600');
    if (!printWin) return;
    
    // Escapar el contenido para poder meterlo en un string literal de JS
    const safeContent = content.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
    const baseUrl = window.location.origin;

    printWin.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            .header-container { display: flex; align-items: center; border-bottom: 2px solid #4f7942; padding-bottom: 15px; margin-bottom: 25px; }
            .header-container img { max-height: 50px; margin-right: 20px; }
            .header-container h2 { color: #4f7942; margin: 0; font-size: 24px; }
            h1, h2, h3, h4 { color: #1f2937; margin-top: 24px; margin-bottom: 12px; }
            p { margin-bottom: 12px; }
            ul, ol { margin-left: 24px; margin-bottom: 16px; }
            li { margin-bottom: 6px; }
            hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; font-size: 14px; }
            th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; }
            th { background-color: #f8fafc; font-weight: bold; }
            strong { font-weight: 600; color: #111827; }
            @media print {
              body { padding: 0; }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
        </head>
        <body>
          <div class="header-container">
            <img src="${baseUrl}/images/Logotipo-RX-big.png" alt="RedXtreme" />
            <h2>${title}</h2>
          </div>
          
          <div id="print-content">Renderizando formato...</div>

          <script>
            // Parse raw markdown using marked
            const rawMarkdown = \`${safeContent}\`;
            document.getElementById('print-content').innerHTML = marked.parse(rawMarkdown);
            
            // Dar 1 segundo para renderizar y cargar logo antes de abrir la ventana de impresión
            setTimeout(() => {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWin.document.close();
  };
  
  // Sale conversion state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [saleDescription, setSaleDescription] = useState('');
  const [paymentPlanType, setPaymentPlanType] = useState('100%');

  // Edit document state
  const [isEditingDoc, setIsEditingDoc] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [approving, setApproving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    loadProposal();
  }, [id]);

  useEffect(() => {
    if (showConvertModal && proposal) {
      let extractedPrice = proposal.commercialOffer?.total || 0;
      let extractedPlan = proposal.commercialOffer?.paymentTerms || '100%';

      // Intento inteligente de extraer el Total si no está crudo en la base de datos
      if (!extractedPrice && (proposal.commercialDoc || proposal.technicalDoc || proposal.aiGeneratedContent)) {
        const txt = proposal.commercialDoc || proposal.technicalDoc || proposal.aiGeneratedContent;
        // Busca expresiones de totales o precios cerca de un número largo formateado
        const priceMatches = [...txt.matchAll(/(?:Total|Inversión|Precio|Costo|Valor)[^\n]{0,30}?(?:\$|COP|USD|€)?\s*([\d.,]{5,})/gi)];
        if (priceMatches.length > 0) {
          // Agarramos el último coincidente, que suele ser el Gran Total
          const lastMatch = priceMatches[priceMatches.length - 1][1];
          // Limpiamos los puntos o comas de separador de miles
          const cleanNum = parseFloat(lastMatch.replace(/[^\d]/g, ''));
          if (!isNaN(cleanNum) && cleanNum > 0) {
            extractedPrice = cleanNum;
          }
        }
        
        // Intentar deducir el plan de pagos del texto
        const planMatch = txt.match(/(100%|50\/50|40\/30\/30|30\/30\/40|40\/40\/20)/);
        if (planMatch && planMatch[1]) {
           extractedPlan = planMatch[1];
        }
      }

      if (extractedPrice > 0) {
        setSalePrice(extractedPrice.toString());
      }
      
      if (proposal.title && (!saleDescription || saleDescription === '')) {
        setSaleDescription(proposal.title);
      }
      
      if (['100%', '50/50', 'CUSTOM', '40/30/30', '40/40/20'].includes(extractedPlan)) {
        setPaymentPlanType(extractedPlan);
      }
    }
  }, [showConvertModal, proposal]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [proposal?.chatHistory]);

  const loadProposal = async () => {
    try {
      setLoading(true);
      const data = await proposalService.getProposalById(id);
      setProposal(data.proposal);
      // init edit content with whatever is currently in working draft
      setEditContent(data.proposal.aiGeneratedContent || '');
    } catch (error) {
      showNotification('Error al cargar la propuesta', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (message = '') => {
    try {
      setGenerating(true);
      const data = await proposalService.generateWithAI(id, message);
      setProposal(data.proposal);
      setEditContent(data.proposal.aiGeneratedContent || ''); // update working draft
      setChatMessage('');
    } catch (error) {
      showNotification(error.message || 'Error al generar con IA', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;
    handleGenerate(chatMessage);
  };

  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);
      await proposalService.updateProposal(id, { aiGeneratedContent: editContent });
      setProposal(prev => ({ ...prev, aiGeneratedContent: editContent }));
      showNotification('Borrador guardado exitosamente.', 'success');
      setIsEditingDoc(false);
    } catch (error) {
      showNotification(error.message || 'Error al guardar borrador', 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleApproveStage = async () => {
    try {
      setApproving(true);
      const data = await proposalService.approveStage(id, { approvedContent: editContent });
      setProposal(data.proposal);
      
      // Auto-populate some defaults if we just moved to completely approved
      if (data.proposal.currentStage === 'COMPLETED') {
        setSaleDescription(`Servicios de Propuesta IA: ${data.proposal.title}`);
      }
      
      showNotification('Etapa aprobada oficialmente.', 'success');
      setIsEditingDoc(false);
    } catch (error) {
      showNotification(error.message || 'Error al aprobar etapa', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleConvertToSale = async (e) => {
    e.preventDefault();
    if (!parseFloat(salePrice) || parseFloat(salePrice) <= 0) {
      return showNotification('Ingresa un precio válido mayor a 0', 'error');
    }
    
    try {
      setConverting(true);
      const data = await proposalService.convertToSale(id, {
        salePrice,
        saleDescription,
        paymentPlanType
      });
      showNotification('¡Propuesta convertida a venta exitosamente!', 'success');
      setShowConvertModal(false);
      setProposal(data.proposal);
    } catch (error) {
      showNotification(error.message || 'Error al convertir a venta', 'error');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <p className="mt-4 text-gray-600">Cargando propuesta...</p>
      </div>
    );
  }

  if (!proposal) {
    return <div className="text-center py-12"><p>Propuesta no encontrada</p></div>;
  }

  const currentStage = proposal.currentStage || 'REQUIREMENTS';
  const currentStageIndex = STAGES.indexOf(currentStage);
  const isCompleted = currentStage === 'COMPLETED';

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center mb-4">
        <button className="btn btn-secondary btn-icon" onClick={() => navigate('/proposals')}>
          <ArrowLeft size={20} /> Volver
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">🤖 {proposal.title}</h1>
          <p className="text-gray-600">Cliente: <strong>{proposal.client?.name}</strong></p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {STAGES.map((s, idx) => (
              <span key={s} className={`badge ${currentStageIndex >= idx ? 'badge-primary' : 'badge-secondary'}`}>
                {STAGE_NAMES[s]}
              </span>
            ))}
          </div>

          {!isCompleted && (
            <button onClick={() => handleGenerate()} disabled={generating} className="btn btn-primary" title="Generar primera respuesta de la etapa actual">
              <Sparkles size={16} /> {generating ? 'Generando...' : 'Iniciar Etapa'}
            </button>
          )}

          {isCompleted && proposal.status !== 'CONVERTED_TO_SALE' && proposal.status !== 'REJECTED' && (
            <div className="flex gap-2">
              <button onClick={handleRejectProposal} className="btn btn-danger btn-outline">
                <XCircle size={16} /> Rechazar
              </button>
              <button onClick={() => setShowConvertModal(true)} className="btn btn-success">
                <ShoppingCart size={16} /> Crear Venta / Facturar
              </button>
            </div>
          )}
          {proposal.status === 'CONVERTED_TO_SALE' && (
             <span className="badge badge-success text-sm py-2 px-3">
               <Check size={16} className="inline mr-1"/>
               Convertida a Venta
             </span>
          )}
          {proposal.status === 'REJECTED' && (
             <span className="badge badge-secondary text-sm py-2 px-3">
               <XCircle size={16} className="inline mr-1"/>
               Propuesta Rechazada
             </span>
          )}
        </div>
      </div>

      {/* Layout: Chat + Documentos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', minHeight: '600px' }}>
        
        {/* Panel izquierdo: Chat con IA */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '720px' }}>
          <div className="card-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '16px', backgroundColor: '#f9fafb' }}>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              💬 Chat Interactivo
              {!isCompleted && <span className="text-sm font-normal text-gray-500">({STAGE_NAMES[currentStage]})</span>}
            </h2>
          </div>
          
          {/* Requerimientos originales (Borrados si ya es técnico o comercial en la vista pero es útil mantenerlos) */}
          <div style={{ padding: '12px 16px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #d1fae5' }}>
            <p className="text-sm font-semibold text-green-700 mb-1">📋 Requerimientos Base</p>
            <p className="text-sm text-green-800" style={{ 
              maxHeight: '60px', overflow: 'auto', whiteSpace: 'pre-wrap' 
            }}>{proposal.requirements}</p>
          </div>

          {/* Historial de chat */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', backgroundColor: '#fff' }}>
            {proposal.chatHistory?.length === 0 && (
              <div className="text-center text-gray-400 py-8">
                <Sparkles size={32} className="mx-auto mb-3" />
                <p>Presiona "Iniciar Etapa" o escribe algo</p>
                <p className="text-sm mt-1">para refinar la etapa actual con la IA.</p>
              </div>
            )}
            {proposal.chatHistory?.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '12px'
              }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: '12px',
                  backgroundColor: msg.role === 'user' ? '#4f7942' : '#f3f4f6',
                  color: msg.role === 'user' ? 'white' : '#1f2937',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  overflowWrap: 'anywhere'
                }} className={msg.role === 'user' ? "" : "prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1"}>
                  {msg.role === 'user' ? (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {generating && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <RefreshCw size={16} className="animate-spin text-primary" />
                  La IA está analizando y construyendo el documento...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input de chat */}
          {!isCompleted && proposal.status !== 'CONVERTED_TO_SALE' && (
            <div style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa', padding: '12px 16px' }}>
              <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                <textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                  placeholder={generating ? 'Esperando respuesta de IA...' : `Afina la etapa: ${STAGE_NAMES[currentStage]}...`}
                  className="form-input"
                  style={{ flex: 1, resize: 'vertical', minHeight: '80px', maxHeight: '200px' }}
                  disabled={generating}
                  rows={3}
                />
                <button type="submit" className="btn btn-primary h-auto py-3" style={{ alignSelf: 'stretch' }} disabled={generating || !chatMessage.trim()}>
                  {generating ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Panel derecho: Documento Oficial de la Etapa Actual */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '720px' }}>
          <div className="card-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={20} />
              Documento Oficial
            </h3>
            
            {/* Toolbar Documento */}
            {!isCompleted && proposal.chatHistory.length > 0 && (
              <div className="flex gap-2">
                {!isEditingDoc ? (
                  <>
                    <button onClick={() => setIsEditingDoc(true)} className="btn btn-secondary btn-icon text-sm py-1 h-auto">
                      <Edit2 size={14} /> Editar
                    </button>
                    <button onClick={handleApproveStage} disabled={approving} className="btn btn-primary text-sm py-1 h-auto">
                      <Check size={14} /> Aprobar {STAGE_NAMES[currentStage]}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setIsEditingDoc(false); setEditContent(proposal.aiGeneratedContent || '') }} className="btn btn-secondary text-sm py-1 h-auto" disabled={savingDraft || approving}>
                      Cancelar
                    </button>
                    <button onClick={handleSaveDraft} disabled={savingDraft || approving} className="btn btn-info text-sm py-1 h-auto">
                      <Save size={14} /> {savingDraft ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button onClick={handleApproveStage} disabled={approving || savingDraft} className="btn btn-primary text-sm py-1 h-auto">
                      <Check size={14} /> Aprobar {STAGE_NAMES[currentStage]}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div style={{ flex: 1, padding: '16px', overflowY: 'auto', backgroundColor: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
            
            {/* VISTA DE DOCUMENTOS YA APROBADOS */}
            {proposal.requirementsDoc && (
               <details className="mb-4 pb-4 border-b border-gray-200">
                 <summary className="text-md font-bold text-green-700 cursor-pointer select-none flex items-center justify-between">
                   <span>✔️ Levantamiento Aprobado</span>
                   <button 
                     onClick={(e) => { e.preventDefault(); printDocument('Levantamiento de Requerimientos', proposal.requirementsDoc); }}
                     className="btn btn-secondary text-xs py-1 px-2 h-auto ml-4"
                     title="Imprimir o Guardar como PDF"
                   >
                     <Printer size={12} className="mr-1" /> Imprimir (PDF)
                   </button>
                 </summary>
                 <div className="mt-3 prose prose-sm max-w-none prose-green">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{proposal.requirementsDoc}</ReactMarkdown>
                 </div>
               </details>
            )}
            {proposal.technicalDoc && (
               <details className="mb-4 pb-4 border-b border-gray-200">
                 <summary className="text-md font-bold text-green-700 cursor-pointer select-none flex items-center justify-between">
                   <span>✔️ Dimensionamiento Aprobado</span>
                   <button 
                     onClick={(e) => { e.preventDefault(); printDocument('Dimensionamiento Técnico', proposal.technicalDoc); }}
                     className="btn btn-secondary text-xs py-1 px-2 h-auto ml-4"
                     title="Imprimir o Guardar como PDF"
                   >
                     <Printer size={12} className="mr-1" /> Imprimir (PDF)
                   </button>
                 </summary>
                 <div className="mt-3 prose prose-sm max-w-none prose-green">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{proposal.technicalDoc}</ReactMarkdown>
                 </div>
               </details>
            )}
            {proposal.commercialDoc && (
               <details className="mb-4 pb-4 border-b border-gray-200">
                 <summary className="text-md font-bold text-green-700 cursor-pointer select-none flex items-center justify-between">
                   <span>✔️ Comercial Aprobado</span>
                   <button 
                     onClick={(e) => { e.preventDefault(); printDocument('Propuesta Comercial', proposal.commercialDoc); }}
                     className="btn btn-secondary text-xs py-1 px-2 h-auto ml-4"
                     title="Imprimir o Guardar como PDF"
                   >
                     <Printer size={12} className="mr-1" /> Imprimir (PDF)
                   </button>
                 </summary>
                 <div className="mt-3 prose prose-sm max-w-none prose-green">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{proposal.commercialDoc}</ReactMarkdown>
                 </div>
               </details>
            )}

            {/* VISTA DEL DOCUMENTO EN CURSO */}
            {!isCompleted && (
              <div className="mt-4 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <h4 className="text-sm font-bold text-gray-500 mb-3 uppercase flex items-center justify-between">
                  En edición: {STAGE_NAMES[currentStage]}
                  {isEditingDoc && <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-1 rounded">Modo Edición Manual</span>}
                </h4>
                
                {generating ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <RefreshCw size={32} className="animate-spin mb-3 text-primary" />
                    <p>Redactando documento oficial...</p>
                  </div>
                ) : proposal.chatHistory.length === 0 ? (
                  <p className="text-center text-gray-400 py-12">El documento aparecerá aquí luego de iniciar la IA.</p>
                ) : isEditingDoc ? (
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="form-input"
                    style={{ 
                      width: '100%', 
                      minHeight: '450px', 
                      fontFamily: '"Inter", -apple-system, sans-serif', 
                      fontSize: '15px',
                      lineHeight: '1.6',
                      padding: '24px',
                      backgroundColor: '#fff',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                    }}
                    placeholder="Escribe el contenido del documento aquí..."
                  />
                ) : (
                  <div className="prose prose-sm max-w-none prose-blue">
                    {proposal.aiGeneratedContent ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{proposal.aiGeneratedContent}</ReactMarkdown>
                    ) : (
                      <span className="text-gray-500">Aún no hay borrador. Pide a la IA que genere el documento.</span>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modal de conversión a venta */}
      <Modal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        title="Crear Venta Oficial"
        onSubmit={handleConvertToSale}
        submitText={converting ? 'Convirtiendo...' : 'Confirmar Venta y Facturar'}
      >
        <div style={{ padding: '0 8px' }}>
          <div className="mb-4" style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <p className="font-medium text-lg mb-1 text-green-800">Has finalizado la Propuesta 🎉</p>
            <p className="text-sm text-green-700">Por favor, extrae manualmente el precio final acordado en la oferta comercial e ingrésalo aquí para generar la Venta en RXApp.</p>
          </div>

          <div className="form-group mb-4">
            <label className="form-label font-bold">Costo Total de la Venta (USD / COP)</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                required
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                className="form-input pl-8"
                placeholder="Ej. 1500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Este será el monto final facturado al cliente.</p>
          </div>

          <div className="form-group mb-4">
            <label className="form-label font-bold">Concepto / Referencia Comercial</label>
            <input
              type="text"
              required
              value={saleDescription}
              onChange={(e) => setSaleDescription(e.target.value)}
              className="form-input"
              placeholder="Ej. Desarrollo Plataforma Web"
            />
          </div>

          <div className="form-group">
            <label className="form-label font-bold">Términos de Pago (Abono Inicial)</label>
            <select
              value={paymentPlanType}
              onChange={(e) => setPaymentPlanType(e.target.value)}
              className="form-input"
            >
              <option value="100%">100% Anticipado</option>
              <option value="50/50">50% Inicial / 50% Final</option>
              <option value="40/30/30">40% / 30% / 30%</option>
              <option value="CUSTOM">Cuotas Custom / Personalizadas</option>
            </select>
            <p className="text-xs text-blue-600 mt-2 bg-blue-50 p-2 rounded">
              👉 <strong>Aviso:</strong> Cuando la factura generada reciba su abono y el estado de la factura sea actualizado, el <strong>Proyecto se creará automáticamente</strong> usando el Documento Técnico.
            </p>
          </div>
        </div>
      </Modal>

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default ProposalDetailPage;
